import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Building2, CreditCard, AlertCircle, ExternalLink, Info } from 'lucide-react';
import { reconnectStripe } from '../../services/stripe-service';
import firestoreService from '../../services/firestore-service';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../config/firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { useScrollToHash } from '../../utils/useScrollToHash';
import LoadingButton from '../../components/common/LoadingButton';
import { useToast } from '../../components/ToastNotification';

const BUSINESS_STRUCTURES = [
  { value: 'sole-proprietor', label: 'Sole Proprietor' },
  { value: 'llc', label: 'LLC (Limited Liability Company)' },
  { value: 's-corp', label: 'S-Corporation' },
  { value: 'not-sure', label: 'Not sure / Haven\'t decided yet' },
];

const BusinessPaymentsSettings = () => {
  useScrollToHash();
  const toast = useToast();
  // Load from localStorage (mock - replace with Firestore in production)
  const [businessInfo, setBusinessInfo] = useState(() => {
    const saved = localStorage.getItem('practitionerBusiness');
    return saved ? JSON.parse(saved) : {
      businessName: '',
      businessStructure: 'sole-proprietor',
      taxId: ''
    };
  });

  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccount, setStripeAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showTaxIdInfo, setShowTaxIdInfo] = useState(false);
  const [showWhyStripe, setShowWhyStripe] = useState(false);

  // Load Stripe connection status from Firestore (with auth-listener pattern)
  useEffect(() => {
    // Check localStorage first for instant display
    const cached = localStorage.getItem('practitioner_stripe_connect');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (parsed.status === 'active') {
          setStripeConnected(true);
          setStripeAccount(parsed);
        }
      } catch (_) {}
    }

    // Then confirm from Firestore once auth is ready
    let done = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (done) return;
      if (!user) return; // Keep listening — session still restoring
      done = true;
      unsubscribe();

      getDoc(doc(db, 'practitioners', user.uid))
        .then((snap) => {
          if (!snap.exists()) return;
          const data = snap.data();
          if (data?.stripeConnect?.status === 'active') {
            setStripeConnected(true);
            setStripeAccount(data.stripeConnect);
            localStorage.setItem('practitioner_stripe_connect', JSON.stringify(data.stripeConnect));
          }
        })
        .catch((err) => console.warn('Firestore Stripe status read failed:', err));
    });

    const timeout = setTimeout(() => { if (!done) done = true; }, 3000);
    return () => { done = true; unsubscribe(); clearTimeout(timeout); };
  }, []);

  // Sync business info from Firestore if localStorage is empty
  useEffect(() => {
    const saved = localStorage.getItem('practitionerBusiness');
    if (!saved) {
      const userId = firestoreService.getCurrentUserId();
      if (userId) {
        firestoreService.getBusinessInfo(userId).then(firestoreData => {
          if (firestoreData && Object.keys(firestoreData).length > 0) {
            localStorage.setItem('practitionerBusiness', JSON.stringify(firestoreData));
            setBusinessInfo(firestoreData);
          }
        }).catch(err => console.warn('Firestore business read failed:', err));
      }
    }
  }, []);

  const handleSave = async () => {
    setIsSaving(true);

    // Validate business name
    if (!businessInfo.businessName.trim()) {
      toast.error('Error', 'Business name is required');
      setIsSaving(false);
      return;
    }

    // Save to localStorage (instant)
    localStorage.setItem('practitionerBusiness', JSON.stringify(businessInfo));

    // Background sync to Firestore
    const userId = firestoreService.getCurrentUserId();
    if (userId) {
      firestoreService.saveBusinessInfo(userId, businessInfo).then(() => {
        console.log('✅ Business info saved to Firestore');
      }).catch(err => {
        console.warn('⚠️ Firestore business save failed:', err);
      });
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));

    setIsSaving(false);
    toast.success('Saved', 'Business settings saved.');
  };

  // TODO: PRODUCTION — Remove test bypass and restore real Stripe Connect flow:
  // const result = await connectStripe();
  // The real flow redirects to Stripe's hosted onboarding page.
  // Restore when Stripe identity verification is resolved or
  // when switching to the Divine Frameworks business Stripe account.
  const handleConnectStripe = async () => {
    setIsConnecting(true);

    try {
      const user = auth?.currentUser;
      if (!user) throw new Error('Not authenticated');

      const testAccountId = 'acct_test_' + user.uid.substring(0, 8);
      const connectData = {
        accountId: testAccountId,
        status: 'active',
        connectedAt: new Date().toISOString(),
        testMode: true,
        chargesEnabled: true,
        payoutsEnabled: true,
      };

      await setDoc(doc(db, 'practitioners', user.uid), {
        stripeConnect: connectData,
        stripeConnected: true,
      }, { merge: true });

      localStorage.setItem('practitioner_stripe_connect', JSON.stringify(connectData));
      setStripeConnected(true);
      setStripeAccount(connectData);
      toast.success('Connected', 'Payment account connected (test mode).');
    } catch (error) {
      console.error('Error connecting test account:', error);
      toast.error('Error', 'Failed to connect payment account. Please try again.');
    }

    setIsConnecting(false);
  };

  const handleDisconnectStripe = async () => {
    if (!window.confirm('Are you sure you want to disconnect Stripe? You won\'t be able to accept payments until you reconnect.')) {
      return;
    }

    try {
      const user = auth?.currentUser;
      if (user) {
        await setDoc(doc(db, 'practitioners', user.uid), {
          stripeConnect: null,
          stripeConnected: false,
        }, { merge: true });
      }
      localStorage.removeItem('practitioner_stripe_connect');
      setStripeConnected(false);
      setStripeAccount(null);
      toast.success('Disconnected', 'Stripe has been disconnected.');
    } catch (error) {
      console.error('Error disconnecting Stripe:', error);
      toast.error('Error', 'Failed to disconnect. Please try again.');
    }
  };

  const handleReconnectStripe = async () => {
    if (!stripeAccount?.accountId) {
      toast.error('Error', 'No account ID found');
      return;
    }

    setIsConnecting(true);

    const result = await reconnectStripe(stripeAccount.accountId);

    if (result.success) {
      toast.info('Reconnecting', result.message || 'Reconnecting to Stripe...');
      // Note: Will redirect to Stripe in real mode
    } else {
      toast.error('Error', result.message || 'Failed to reconnect. Please try again.');
    }

    setIsConnecting(false);
  };

  const updateField = (field, value) => {
    setBusinessInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Business & Payments</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Business & Payments</h1>
          <p className="text-gray-600">Manage your business information and payment settings</p>
        </div>

        <div className="space-y-6">
          {/* Section 1: Business Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Business Information</h2>
                <p className="text-sm text-gray-600">This information appears on receipts and invoices</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={businessInfo.businessName}
                  onChange={(e) => updateField('businessName', e.target.value)}
                  placeholder="Your business or professional name"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is how your business appears on client receipts
                </p>
              </div>

              {/* Business Structure */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Structure
                </label>
                <select
                  value={businessInfo.businessStructure}
                  onChange={(e) => updateField('businessStructure', e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none transition-all bg-white"
                >
                  {BUSINESS_STRUCTURES.map(structure => (
                    <option key={structure.value} value={structure.value}>
                      {structure.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This helps with tax reporting and legal compliance
                </p>
              </div>

              {/* Tax ID / EIN */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Tax ID / EIN (Optional)
                  </label>
                  <button
                    onClick={() => setShowTaxIdInfo(!showTaxIdInfo)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>

                {showTaxIdInfo && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-xs text-blue-800 leading-relaxed">
                      Your Tax ID (EIN or SSN) is only needed if you want it displayed on client receipts.
                      This information is encrypted and never shared with third parties.
                    </p>
                  </div>
                )}

                <input
                  type="text"
                  value={businessInfo.taxId}
                  onChange={(e) => updateField('taxId', e.target.value)}
                  placeholder="XX-XXXXXXX"
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-purple-600 focus:ring-1 focus:ring-purple-600 outline-none transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only include this if required for your business type
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Stripe Connection */}
          <div id="stripe-connect" className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Payment Processing</h2>
                <p className="text-sm text-gray-600">Connect Stripe to accept payments from clients</p>
              </div>
            </div>

            {!stripeConnected ? (
              // Not connected state
              <div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <svg className="w-12 h-12" viewBox="0 0 60 25" fill="none">
                        <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 01-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 013.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 01-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 01-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 00-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.85 6.29.97 6.29 5.88z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-2">
                        Connect with Stripe
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Stripe is a secure payment processor trusted by millions of businesses.
                        You'll keep 100% of your session fees — we never take a cut.
                      </p>
                      <ul className="space-y-2 text-sm text-gray-600 mb-6">
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Accept credit cards, debit cards, and digital wallets</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Automatic payouts to your bank account</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Bank-level security and fraud protection</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>Standard processing: 2.9% + 30¢ per transaction</span>
                        </li>
                      </ul>
                      <LoadingButton
                        onClick={handleConnectStripe}
                        loading={isConnecting}
                        loadingText="Connecting..."
                        className="px-6 py-3 bg-[#635bff] text-white font-semibold rounded-lg hover:bg-[#5249e0] transition-all flex items-center gap-2"
                      >
                        <CreditCard className="w-4 h-4" />
                        Connect Stripe Account
                      </LoadingButton>

                      <button
                        onClick={() => setShowWhyStripe(!showWhyStripe)}
                        className="text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors mt-3 bg-transparent border-0 p-0 outline-none focus:outline-none cursor-pointer"
                        type="button"
                      >
                        <span>{showWhyStripe ? 'Hide details' : 'Why Stripe?'}</span>
                      </button>

                      {showWhyStripe && (
                        <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg space-y-4 text-sm">
                          {/* Trust & Scale */}
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Trusted by millions of businesses</p>
                            <p className="text-gray-600">
                              Stripe processes over $640 billion annually for businesses worldwide,
                              powering major platforms and independent practitioners alike.
                            </p>
                          </div>

                          {/* Spiritual Services */}
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Built for service-based businesses</p>
                            <p className="text-gray-600">
                              Stripe welcomes wellness and spiritual practitioners.
                              Your services are legitimate business, and you'll be treated as a valued merchant.
                            </p>
                          </div>

                          {/* Direct Deposits */}
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Money goes directly to your bank</p>
                            <p className="text-gray-600">
                              Divine Frameworks never touches your money. When a client pays,
                              funds go directly from their card to your bank account (minus standard processing fees).
                              You're in complete control.
                            </p>
                          </div>

                          {/* Dispute Protection */}
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Dispute protection included</p>
                            <p className="text-gray-600">
                              If a client disputes a charge, Stripe provides tools and guidance to resolve it.
                              Clear cancellation policies and session documentation (which Divine Frameworks helps you maintain)
                              protect you in most cases.
                            </p>
                          </div>

                          {/* Security */}
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Bank-level security</p>
                            <p className="text-gray-600">
                              Stripe is certified to the highest level of payment security (PCI Level 1).
                              Your banking information is encrypted and never shared.
                              Clients never see your personal details.
                            </p>
                          </div>

                          {/* Transparent Fees */}
                          <div>
                            <p className="font-medium text-gray-900 mb-1">Simple, transparent pricing</p>
                            <p className="text-gray-600">
                              Standard rate: 2.9% + 30¢ per successful transaction.
                              No monthly fees, no setup fees, no hidden costs. You only pay when you get paid.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      You won't be able to accept paid bookings until you connect a payment processor.
                    </span>
                  </p>
                </div>
              </div>
            ) : (
              // Connected state
              <div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <Check className="w-6 h-6 text-green-600" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base font-semibold text-green-900 mb-1">
                          Stripe Connected{stripeAccount?.testMode ? ' (Test Mode)' : ''}
                        </h3>
                        <p className="text-sm text-green-700 mb-4">
                          You're all set to accept payments from clients
                        </p>

                        {stripeAccount && (
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Account ID:</span>
                              <span className="font-medium text-gray-900 font-mono">{stripeAccount.accountId}</span>
                            </div>
                            {stripeAccount.email && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Account Email:</span>
                                <span className="font-medium text-gray-900">{stripeAccount.email}</span>
                              </div>
                            )}
                            {stripeAccount.bankAccount && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-600">Bank Account:</span>
                                <span className="font-medium text-gray-900">
                                  •••• {stripeAccount.bankAccount.last4}
                                </span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">Payout Schedule:</span>
                              <span className="font-medium text-gray-900">{stripeAccount.payoutSchedule || 'Daily'}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <a
                    href="https://dashboard.stripe.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Stripe Dashboard
                  </a>
                  <span className="text-gray-300">|</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={!isConnecting ? handleReconnectStripe : undefined}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isConnecting) handleReconnectStripe(); }}
                    onMouseEnter={(e) => { if (!isConnecting) e.currentTarget.style.color = '#6D28D9'; }}
                    onMouseLeave={(e) => { if (!isConnecting) e.currentTarget.style.color = '#7c3aed'; }}
                    className="text-sm font-medium"
                    style={{ color: '#7c3aed', cursor: isConnecting ? 'not-allowed' : 'pointer', opacity: isConnecting ? 0.5 : 1, userSelect: 'none', transition: 'color 0.15s ease' }}
                  >
                    {isConnecting ? 'Reconnecting...' : 'Reconnect / Update Info'}
                  </span>
                  <span className="text-gray-300">|</span>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleDisconnectStripe}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleDisconnectStripe(); }}
                    className="text-sm text-red-600 hover:text-red-700 font-medium transition-colors"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Disconnect
                  </span>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">About Payouts</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Stripe automatically transfers your earnings to your bank account based on your payout schedule.
                    The default is daily (2-day rolling basis), meaning funds from today's sessions will arrive in 2 business days.
                    You can change your payout schedule in your Stripe Dashboard.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <Link
              to="/dashboard/settings"
              className="px-6 py-2.5 text-gray-700 hover:text-gray-900 font-medium transition-colors"
            >
              Cancel
            </Link>
            <LoadingButton
              onClick={handleSave}
              loading={isSaving}
              loadingText="Saving..."
              className="px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all flex items-center gap-2"
            >
              Save Changes
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessPaymentsSettings;
